
import React, { useEffect } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Star, Quote } from 'lucide-react';
import { Link } from 'react-router-dom';

const testimonials = [
  {
    name: "Margaret Hutchinson",
    location: "Easton, CT",
    text: "PK Landscape Associates transformed our five-acre estate into a masterpiece. The attention to detail in the formal garden design and the native plantings around our pond exceeded all expectations. Their team is professional, punctual, and truly understands luxury landscaping.",
    project: "Estate Landscape Design",
    rating: 5
  },
  {
    name: "Robert & Christine Walsh",
    location: "Trumbull, CT", 
    text: "After interviewing several landscapers, we chose PK for our complete backyard renovation. The outdoor kitchen, bluestone patio, and fire pit area they designed has become the heart of our home. We entertain there constantly and receive endless compliments.",
    project: "Outdoor Living Space",
    rating: 5
  },
  {
    name: "David Chen",
    location: "Monroe, CT",
    text: "We needed a landscape company that could handle our large property and understand our desire for privacy while maintaining an elegant appearance. PK delivered with strategic plantings and a stunning entrance that sets our home apart. Highly recommend.",
    project: "Privacy Landscape Design",
    rating: 5
  },
  {
    name: "Jennifer Morrison",
    location: "Fairfield, CT",
    text: "The maintenance team from PK has been caring for our property for three years now. They're reliable, thorough, and our lawn and gardens have never looked better. The irrigation system they installed has made a tremendous difference during summer months.",
    project: "Lawn Maintenance & Irrigation",
    rating: 5
  },
  {
    name: "Stratford Commerce Center",
    location: "Stratford, CT",
    text: "As property managers, we demand consistency and professionalism. PK Landscape Associates has maintained our office park for two years, and tenant satisfaction with the grounds has never been higher. Their snow removal response is exceptional.",
    project: "Commercial Property Maintenance",
    rating: 5
  },
  {
    name: "The Hendricks Family",
    location: "Shelton, CT",
    text: "From the initial design consultation to the final walkthrough, working with PK was a pleasure. They listened to our ideas, offered creative solutions, and delivered a landscape that perfectly complements our colonial home. Worth every penny.",
    project: "Complete Landscape Renovation",
    rating: 5
  },
  {
    name: "Patricia & Thomas Reynolds",
    location: "Bridgeport, CT",
    text: "PK redesigned our dated landscape into a modern, low-maintenance retreat. The hardscape work is impeccable, and the plant selections provide year-round interest. Their knowledge of what thrives in Connecticut's climate really shows.",
    project: "Landscape Redesign",
    rating: 5
  },
  {
    name: "Westbrook Condominiums HOA",
    location: "Trumbull, CT",
    text: "Our condominium association switched to PK Landscape Associates two years ago, and the improvement in our grounds is remarkable. They're responsive, professional, and have helped increase our property values through consistent, quality maintenance.",
    project: "HOA Grounds Maintenance",
    rating: 5
  }
];

const TestimonialsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Header */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-pkgreen-50 to-white relative">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <span className="text-pkgreen-600 font-display uppercase tracking-wider text-sm mb-2 block animate-fade-up">
              Client Testimonials
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold mb-6 text-pkstone-900 animate-fade-up">
              What Our Clients Say
            </h1>
            <p className="text-xl text-pkstone-700 animate-fade-up max-w-2xl mx-auto">
              Read what homeowners and businesses throughout Greater Bridgeport and Fairfield County 
              have to say about their experience working with PK Landscape Associates.
            </p>
          </div>
        </div>
      </section>
      
      {/* Testimonials Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {testimonials.map((testimonial, index) => (
                <div 
                  key={index} 
                  className="bg-white p-8 shadow-lg rounded-lg border border-pkstone-100 hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="flex items-start mb-4">
                    <Quote className="w-10 h-10 text-pkgreen-200 mr-4 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 text-pkearth-400 fill-current" />
                        ))}
                      </div>
                      <p className="text-pkstone-700 italic leading-relaxed">
                        "{testimonial.text}"
                      </p>
                    </div>
                  </div>
                  <div className="border-t border-pkstone-100 pt-4 mt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-serif font-bold text-pkstone-900">{testimonial.name}</h3>
                        <p className="text-sm text-pkstone-500">{testimonial.location}</p>
                      </div>
                      <span className="text-xs bg-pkgreen-50 text-pkgreen-700 px-3 py-1 rounded-full">
                        {testimonial.project}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-pkstone-900 relative">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1558293842-c0fd3db86157?q=80&w=2069&auto=format&fit=crop')] bg-cover bg-center opacity-20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6 text-white">
              Ready to Join Our Satisfied Clients?
            </h2>
            <p className="text-xl text-pkstone-200 mb-8">
              Experience the PK Landscape Associates difference. Contact us today to schedule 
              a consultation and discover how we can transform your property.
            </p>
            <Link
              to="/contact"
              className="bg-pkgreen-600 hover:bg-pkgreen-700 text-white py-3 px-8 rounded-md inline-block font-medium transition-all duration-300"
            >
              Request Your Consultation
            </Link>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default TestimonialsPage;
